import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, X, Send, Loader2, Bot, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const renderMessageContent = (content) => {
  if (typeof content !== 'string') return content;
  
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(content.substring(lastIndex, match.index));
    }
    const text = match[1];
    const url = match[2];
    
    if (url.startsWith('/')) {
      parts.push(<Link key={match.index} to={url} className="underline text-indigo-400 hover:text-indigo-300 font-bold">{text}</Link>);
    } else {
      parts.push(<a key={match.index} href={url} target="_blank" rel="noopener noreferrer" className="underline text-indigo-400 hover:text-indigo-300 font-bold">{text}</a>);
    }
    lastIndex = linkRegex.lastIndex;
  }

  if (lastIndex < content.length) {
    parts.push(content.substring(lastIndex));
  }

  return parts;
};

const ChatbotFAB = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const [isChatbotEnabled, setIsChatbotEnabled] = useState(user?.chatbot_enabled !== false);
  const messagesEndRef = useRef(null);

  // Fetch initial chatbot status and listen for WebSocket events
  useEffect(() => {
    const fetchChatbotStatus = async () => {
      try {
        const response = await api.get('/chat/status');
        setIsChatbotEnabled(response.data.is_enabled);
      } catch (error) {
        console.error('Failed to fetch chatbot status:', error);
      }
    };

    fetchChatbotStatus();

    const handlePermissionChange = (event) => {
      setIsChatbotEnabled(event.detail.isEnabled);
      if (!event.detail.isEnabled) {
        setIsOpen(false);
      }
    };

    window.addEventListener('chatbot-permission-changed', handlePermissionChange);
    return () => {
      window.removeEventListener('chatbot-permission-changed', handlePermissionChange);
    };
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading, isOpen]);

  if (!user || !isChatbotEnabled) return null; // Don't show if not logged in or disabled

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await api.post('/chat', {
        messages: [...messages, userMessage].map((m) => ({
          role: m.role,
          content: m.content,
        })),
      });
      
      const aiMessage = { role: 'assistant', content: response.data.response };
      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to communicate with AI assistant.');
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Sorry, I encountered an error. Please check the backend console or ensure GROQ_API_KEY is configured.' }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Header Button with Glow */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className={`relative h-9 sm:h-10 w-9 sm:w-10 transition-all duration-300 ${isOpen ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20' : 'text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 hover:bg-indigo-50/50'}`}
        data-testid="chatbot-trigger"
      >
        {/* Continuous glowing backdrop when closed */}
        {!isOpen && (
          <div className="absolute inset-0 rounded-md animate-pulse shadow-[0_0_15px_rgba(79,70,229,0.5)] dark:shadow-[0_0_15px_rgba(99,102,241,0.4)] border border-indigo-400/30 pointer-events-none"></div>
        )}
        <div className="relative z-10 flex items-center justify-center">
          {isOpen ? <X size={18} className="sm:w-5 sm:h-5" /> : <Bot size={18} className="sm:w-5 sm:h-5 drop-shadow-[0_0_8px_rgba(79,70,229,0.6)]" />}
        </div>
      </Button>

      {/* Chat Window */}
      {isOpen && (
        <Card className="fixed top-16 right-4 sm:right-6 w-80 sm:w-96 h-[500px] flex flex-col shadow-xl z-50 border border-indigo-200 dark:border-indigo-900 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl overflow-hidden animate-in slide-in-from-top-5 fade-in-20 duration-300 rounded-2xl">
          <CardHeader className="bg-gradient-to-r from-indigo-600 via-blue-600 to-indigo-600 p-4 shrink-0 shadow-sm relative overflow-hidden">
            <div className="absolute inset-0 bg-white/10 backdrop-blur-sm transform -skew-x-12 translate-x-full hover:translate-x-0 transition-transform duration-1000"></div>
            <CardTitle className="text-white flex items-center gap-2 text-lg relative z-10 font-bold tracking-wide">
              <Bot size={22} className="text-blue-100 drop-shadow-md" />
              PulseDesk AI
            </CardTitle>
          </CardHeader>

          <CardContent className="flex-1 p-0 overflow-y-auto custom-scrollbar">
            <div className="h-full p-4">
              <div className="space-y-4 flex flex-col min-h-full justify-end">
                {messages.length === 0 && (
                  <div className="text-center text-slate-500 dark:text-slate-400 mt-10">
                    <Bot size={40} className="mx-auto mb-2 opacity-50" />
                    <p>Ask me about your ticketing insights!</p>
                  </div>
                )}
                
                {messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-2 max-w-[85%] ${
                      msg.role === 'user' ? 'ml-auto flex-row-reverse' : 'mr-auto'
                    }`}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      msg.role === 'user' ? 'bg-slate-100 dark:bg-slate-800' : 'bg-indigo-100 dark:bg-indigo-900/50'
                    }`}>
                      {msg.role === 'user' ? <User size={16} className="text-slate-600 dark:text-slate-300" /> : <Bot size={16} className="text-indigo-600 dark:text-indigo-400" />}
                    </div>
                    <div
                      className={`p-3 rounded-xl text-sm whitespace-pre-wrap shadow-sm ${
                        msg.role === 'user'
                          ? 'bg-blue-600 text-white rounded-tr-none'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-tl-none border border-slate-200 dark:border-slate-700'
                      }`}
                    >
                      {renderMessageContent(msg.content)}
                    </div>
                  </div>
                ))}
                
                {isLoading && (
                  <div className="flex items-start gap-2 mr-auto max-w-[85%]">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center shrink-0 shadow-sm border border-indigo-200 dark:border-indigo-800">
                      <Bot size={16} className="text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div className="p-4 rounded-xl rounded-tl-none bg-slate-50 dark:bg-slate-800/80 border border-indigo-100 dark:border-indigo-900/50 shadow-sm">
                      <Loader2 size={16} className="animate-spin text-indigo-600 dark:text-indigo-400" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>
          </CardContent>

          <CardFooter className="p-3 shrink-0 border-t border-indigo-100 dark:border-indigo-900/50 bg-slate-50/50 dark:bg-slate-900/50 backdrop-blur-md">
            <form
              className="flex w-full gap-2 items-center"
              onSubmit={(e) => {
                e.preventDefault();
                handleSend();
              }}
            >
              <Input
                placeholder="Ask about tickets..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                disabled={isLoading}
                className="flex-1"
                autoFocus
              />
              <Button type="submit" disabled={!input.trim() || isLoading} size="icon" className="shrink-0 bg-indigo-600 hover:bg-indigo-700">
                <Send size={16} />
              </Button>
            </form>
          </CardFooter>
        </Card>
      )}
    </>
  );
};

export default ChatbotFAB;
